import { List } from "semantic-ui-react";

import { selectColumn } from "components/BaseTableV3/selectColumn";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import { formatDate } from "components/helpers";
import MachineService from "services/Machine";

const renderCoordCell = (value, original, openMapModal) => {
  if (!value) {
    return null;
  }
  const [lat, lng] = value.split(",");
  return (
    <List>
      <List.Item>
        <a
          onClick={() =>
            openMapModal({
              modelType: "Case Event",
              modelId: original.id,
              locations: [
                {
                  data: original,
                  lat: parseFloat(lat),
                  lng: parseFloat(lng),
                },
              ],
            })
          }
        >
          {value}
        </a>
      </List.Item>
    </List>
  );
};

export const getCaseEventsTableColumns = ({ openMapModal }) => {
  return [
    selectColumn,
    {
      header: "Case ID",
      id: "Case ID",
      accessorKey: "case_id",
      size: 100,
    },
    {
      header: "Machine ID",
      id: "Machine ID",
      accessorKey: "machine_id",
      size: 100,
    },
    {
      header: "Product",
      id: "Product",
      accessorKey: "product",
    },
    {
      header: "Event Type",
      id: "Event Type",
      accessorKey: "event_type",
    },
    {
      header: "Server Timestamp",
      id: "Server Timestamp",
      accessorKey: "server_timestamp",
      size: 232,
      cell: ({ row }) => {
        const value = row?.original?.server_timestamp;
        return value ? formatDate(value) : null;
      },
    },
    {
      header: "Machine Local Timestamp",
      id: "Machine Local Timestamp",
      accessorKey: "machine_local_timestamp",
      size: 232,
      cell: ({ row }) => {
        const value = row?.original?.machine_local_timestamp;
        return value ? formatDate(value) : null;
      },
    },
    {
      header: "Machine Local Timezone",
      id: "Machine Local Timezone",
      accessorKey: "machine_local_timezone",
    },
    {
      header: "Computer Domain",
      id: "Computer Domain",
      accessorKey: "computer_domain",
    },
    {
      header: "Email Domains",
      id: "Email Domains",
      accessorKey: "email_domains",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {(row.getValue("Email Domains") || []).map((item, idx) => (
            <li key={`${item}-${idx}`}>{item}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Machine Locale",
      id: "Machine Locale",
      accessorKey: "machine_locale",
    },
    {
      header: "Active Mac",
      id: "Active Mac",
      accessorKey: "active_mac",
    },
    {
      header: "WiFi Lat/Long",
      id: "WiFi Lat/Long",
      accessorKey: "wifi_lat_long",
      enableSorting: false,
      cell: ({ row }) =>
        renderCoordCell(
          row?.original?.wifi_lat_long,
          row.original,
          openMapModal
        ),
    },
    {
      header: "IP Lat/Long",
      id: "IP Lat/Long",
      accessorKey: "ip_lat_long",
      enableSorting: false,
      cell: ({ row }) =>
        renderCoordCell(row?.original?.ip_lat_long, row.original, openMapModal),
    },
    {
      header: "Hostname",
      id: "Hostname",
      accessorKey: "hostname",
    },
    {
      header: "Username",
      id: "Username",
      accessorKey: "username",
    },
    {
      header: "Active WiFi Access Point",
      id: "Active WiFi Access Point",
      accessorKey: "active_wifi_access_point",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {(row.getValue("Active WiFi Access Point") || []).map((item, idx) => (
            <li key={`${item}-${idx}`}>{item}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Client Email Address",
      id: "Client Email Address",
      accessorKey: "client_email_address",
    },
    {
      header: "Additional Email Addresses",
      id: "Additional Email Addresses",
      accessorKey: "additional_email_addresses",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {(row.getValue("Additional Email Addresses") || []).map(
            (item, idx) => (
              <li key={`${item}-${idx}`}>{item}</li>
            )
          )}
        </ScrollColumn>
      ),
    },
    {
      header: "Public IP Organization",
      id: "Public IP Organization",
      accessorKey: "public_ip_organization",
    },
    {
      header: "Version",
      id: "Version",
      accessorKey: "version",
    },
    {
      header: "DNS Domain",
      id: "DNS Domain",
      accessorKey: "dns_domain",
    },
    {
      header: "Licensed To",
      id: "Licensed To",
      accessorKey: "licensed_to",
    },
    {
      header: "License Info",
      id: "License Info",
      accessorKey: "license_info",
    },
    {
      header: "Additional License Info",
      id: "Additional License Info",
      accessorKey: "additional_license_info",
    },
    {
      header: "Public IP State",
      id: "Public IP State",
      accessorKey: "public_ip_state",
    },
    {
      header: "Public IP City",
      id: "Public IP City",
      accessorKey: "public_ip_city",
    },
    {
      header: "Public IP Address",
      id: "Public IP Address",
      accessorKey: "public_ip_address",
    },
  ];
};

export const getCaseEventsTableFilters = ({ caseId, productLineId }) => {
  const nullNotNullOptions = [
    {
      id: "null",
      name: "Is null",
    },
    {
      id: "not_null",
      name: "Is not null",
    },
  ];

  return [
    {
      key: "machine_id",
      title: "Machine ID",
      type: "ruvixxSelect",
      queryFn: filters =>
        MachineService.getForFilters({
          case_id: caseId,
          product_line_id: productLineId,
          ...filters,
        }),
      props: {
        includeNoneOption: false,
      },
    },
    {
      key: "server_timestamp",
      title: "Server Timestamp",
      type: "dateRange",
    },
    {
      key: "email_domains",
      title: "Email Domains",
      type: "select",
      data: nullNotNullOptions,
      isSingle: true,
      clearable: true,
    },
    {
      key: "computer_domain",
      title: "Computer Domain",
      type: "select",
      data: nullNotNullOptions,
      isSingle: true,
      clearable: true,
    },
    {
      key: "wifi_lat_long",
      title: "WiFi Lat/Long",
      type: "select",
      data: nullNotNullOptions,
      isSingle: true,
      clearable: true,
    },
    {
      key: "ip_lat_long",
      title: "IP Lat/Long",
      type: "select",
      data: nullNotNullOptions,
      isSingle: true,
      clearable: true,
    },
    {
      key: "search:hostname",
      title: "Hostname",
      type: "input",
    },
    {
      key: "search:username",
      title: "Username",
      type: "input",
    },
    {
      key: "search:public_ip_organization",
      title: "Public IP Organization",
      type: "input",
    },
    {
      key: "search:version",
      title: "Version",
      type: "input",
    },
    {
      key: "search:dns_domain",
      title: "DNS Domain",
      type: "input",
    },
    {
      key: "search:licensed_to",
      title: "Licensed To",
      type: "input",
    },
    {
      key: "search:license_info",
      title: "License Info",
      type: "input",
    },
    {
      key: "search:additional_license_info",
      title: "Additional License Info",
      type: "input",
    },
    {
      key: "search:public_ip_state",
      title: "Public IP State",
      type: "input",
    },
    {
      key: "search:public_ip_city",
      title: "Public IP City",
      type: "input",
    },
    {
      key: "search:public_ip_address",
      title: "Public IP Address",
      type: "input",
    },
    {
      key: "search:client_email_address",
      title: "Client Email Address",
      type: "input",
    },
  ];
};
